/**
 * Model catalog page — `GET /llm-providers?isActive=true`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` + `CordioAbpPagedDataTable`, with the grid owning paging, search, sorting and
 * filtering. The old page was a stack of expandable provider cards, each wrapping its own MUI
 * table, with a page-level `searchTerm` that filtered both levels at once and no paging at all.
 *
 * The rows are now models, not providers: one flat, pageable list with the provider as a column.
 * That is the change that makes "which models have a 200K context?" answerable — the question the
 * per-provider accordions made hardest.
 *
 * WHY THE PAGING IS LOCAL: `/llm-providers` is not a paged endpoint. It returns every provider with
 * its models nested, in one response, and there is no `/llm-models` variant that pages either. So
 * the catalog is fetched once, flattened, and then searched / filtered / sorted / sliced in memory
 * — but still through {@link createCortexPagedDataSource}, so the offset→page arithmetic is the
 * shared, tested one rather than a second implementation living here.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field             │ header   │ width │ cell type              │ filter
 * ──────────────────┼──────────┼───────┼────────────────────────┼───────────────────────────────────
 * displayName       │ Model    │  22%  │ text (sortable)        │ toolbar search (local)
 * name              │ Model ID │  22%  │ text (sortable)        │ toolbar search (local)
 * providerName      │ Provider │  16%  │ text (sortable)        │ popover select → `providerId`
 * maxContextTokens  │ Context  │  12%  │ custom (128K, sortable)│ —
 * costPerInputToken │ Cost /1M │  16%  │ custom (in → out)      │ —
 * isActive          │ Status   │  12%  │ custom Chip            │ status tabs → synthetic `status`
 *
 * Dropped from the old 7-column per-provider table: `maxOutputTokens` (the context window is the
 * number people choose a model on; the output cap is a detail-page fact) and the separate
 * input/output cost columns — those are folded into one "in → out" cell, which keeps both numbers
 * without spending two of six columns on them. The provider-level System/Custom chip and the
 * expand/collapse per provider go with the accordions; provider identity is now a column and a
 * filter.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * This page is read-only — the catalog is seeded by the platform, and provider credentials are
 * configured under Tenant Settings — so it carries no row actions and no `DeleteDialog`.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import {
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';

import { createCortexPagedDataSource, type CortexPagedQuery } from '../components/data-display/cortexPagedDataSource';
import { llmService } from '../services/llmService';
import { useNotificationStore } from '../store/notificationStore';

import type { LlmModelDto, LlmProviderDto } from '../types/llm';
import type { PagedResult } from '../types/tenant';

import './ModelCatalogPage.scss';

/**
 * Quick-filter tabs over the model's `isActive` dimension.
 *
 * The All tab declares the literal `'all'` because the grid builds each tab's test id as
 * `cordio-datatable-status-tab-<value>`; an empty value emits `cordio-datatable-status-tab-` and
 * GRID-13's `…-status-tab-all` selector matches nothing.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/**
 * Synthetic toolbar key the status tabs write to. `'all'` is a UI concept, not a filter value, so
 * {@link toModelQuery} turns the tab into a boolean `isActive` and drops it entirely for All — the
 * same translation the server-backed list pages do, kept here so the toolbar behaves identically.
 */
const STATUS_TAB_PARAM_KEY = 'status';

/** The sort fields the local comparator understands; anything else falls back to catalog order. */
const SORTABLE_FIELDS = ['displayName', 'name', 'providerName', 'maxContextTokens'] as const;

/** Formats a token count for display (128000 → "128K"). */
function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K`;
  return String(tokens);
}

/**
 * Formats a rate for display. Rates are stored as USD per 1,000,000 tokens — the headline vendor
 * rate — so they are shown as plain dollars and the "/1M" lives in the column header.
 */
function formatCost(cost?: number): string {
  if (cost === undefined || cost === null) return '—';
  if (cost === 0) return 'Free';
  return `$${cost.toFixed(cost < 1 ? 4 : 2)}`;
}

/** Normalises the grid's query: the synthetic `status` tab becomes a boolean `isActive`. */
function toModelQuery(query: CortexPagedQuery): CortexPagedQuery {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;

  if (status === 'true' || status === 'false') {
    return { ...rest, isActive: status === 'true' } as CortexPagedQuery;
  }
  return rest as CortexPagedQuery;
}

/**
 * Flattens the provider tree into model rows.
 *
 * `providerName` is overwritten with the provider's `displayName`: the DTO's own `providerName` is
 * the machine name (`openai`), and the column — and the search the user types into — should be the
 * label they see everywhere else ("OpenAI").
 */
function flattenModels(providers: readonly LlmProviderDto[]): LlmModelDto[] {
  return providers.flatMap((provider) =>
    provider.models.map((model) => ({ ...model, providerName: provider.displayName })),
  );
}

/** Applies the grid's search / filter / sort / page to the in-memory catalog. */
function pageModels(providers: readonly LlmProviderDto[], query: CortexPagedQuery): PagedResult<LlmModelDto> {
  const term = typeof query.searchTerm === 'string' ? query.searchTerm.trim().toLowerCase() : '';
  const providerId = typeof query.providerId === 'string' ? query.providerId : undefined;
  const isActive = typeof query.isActive === 'boolean' ? query.isActive : undefined;

  let rows = flattenModels(providers);

  if (term) {
    rows = rows.filter(
      (row) =>
        row.name.toLowerCase().includes(term) ||
        row.displayName.toLowerCase().includes(term) ||
        row.providerName.toLowerCase().includes(term),
    );
  }
  if (providerId) {
    rows = rows.filter((row) => row.providerId === providerId);
  }
  if (isActive !== undefined) {
    rows = rows.filter((row) => row.isActive === isActive);
  }

  const sortBy = SORTABLE_FIELDS.find((field) => field === query.sortBy);
  if (sortBy) {
    const direction = query.sortDescending ? -1 : 1;
    rows = [...rows].sort((a, b) => {
      const left = a[sortBy];
      const right = b[sortBy];
      if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction;
      return String(left).localeCompare(String(right)) * direction;
    });
  }

  const pageSize = query.pageSize > 0 ? query.pageSize : 25;
  const start = (query.pageNumber - 1) * pageSize;
  const items = rows.slice(start, start + pageSize);

  return {
    items,
    totalCount: rows.length,
    pageNumber: query.pageNumber,
    pageSize,
    totalPages: Math.max(1, Math.ceil(rows.length / pageSize)),
    hasPreviousPage: query.pageNumber > 1,
    hasNextPage: start + pageSize < rows.length,
  };
}

export const ModelCatalogPage = () => {
  const { showNotification } = useNotificationStore();

  /** `null` until the catalog resolves, so the summary tiles never flash zeroes. */
  const [providers, setProviders] = useState<LlmProviderDto[] | null>(null);

  /**
   * The single in-flight/settled catalog request, shared by the grid's `dataSource` and the
   * Provider filter's lazy options. Without it, opening the filter popover would refetch the whole
   * catalog. Cleared on failure so the grid's Refresh control can retry.
   */
  const catalogRef = useRef<Promise<LlmProviderDto[]> | null>(null);

  const loadCatalog = useCallback(() => {
    if (!catalogRef.current) {
      catalogRef.current = llmService
        .getProviders(true)
        .then((loaded) => {
          setProviders(loaded);
          return loaded;
        })
        .catch((err: unknown) => {
          catalogRef.current = null;
          showNotification(err instanceof Error ? err.message : 'Failed to load model catalog', 'error');
          // Rethrown so the grid shows its own error state rather than an empty table.
          throw err;
        });
    }
    return catalogRef.current;
  }, [showNotification]);

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<LlmModelDto>(async (query) => {
        const loaded = await loadCatalog();
        return pageModels(loaded, toModelQuery(query));
      }),
    [loadCatalog],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<LlmModelDto>>>(
    () => [
      { field: 'displayName', header: 'Model', type: 'text', width: '22%', sortable: true },
      // The literal string sent to the provider's API — what a misconfigured agent shows in its
      // error, so it has to be greppable from this list.
      { field: 'name', header: 'Model ID', type: 'text', width: '22%', sortable: true },
      { field: 'providerName', header: 'Provider', type: 'text', width: '16%', sortable: true },
      {
        field: 'maxContextTokens',
        header: 'Context',
        type: 'custom',
        width: '12%',
        sortable: true,
        // Abbreviated: "128K" is comparable at a glance in a way that "128000" is not.
        customCell: (row) => <>{formatTokens(row.maxContextTokens)}</>,
      },
      {
        field: 'costPerInputToken',
        header: 'Cost /1M (in → out)',
        type: 'custom',
        width: '16%',
        // Both rates in one cell: they are always read as a pair, and splitting them would spend
        // two of six columns on one idea.
        customCell: (row) => <>{`${formatCost(row.costPerInputToken)} → ${formatCost(row.costPerOutputToken)}`}</>,
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search providers or models' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Model status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        {
          key: 'providerId',
          label: 'Provider',
          type: 'select',
          // Reuses the already-resolved catalog request, so opening the popover costs nothing.
          loadOptions: async () => {
            const loaded = await loadCatalog();
            return loaded.map((provider) => ({ label: provider.displayName, value: provider.id }));
          },
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [loadCatalog],
  );

  return (
    <div className="model-catalog" data-testid="model-catalog-list-page">
      <ShellHeader
        title="Model Catalog"
        description="Every model this deployment can call. Configure provider credentials in Tenant Settings to use them."
        data-testid="model-catalog-list-header"
      />

      {/* Catalog-wide totals the grid cannot express: it pages models, not providers. */}
      {providers && (
        <dl className="model-catalog__stats">
          <div className="model-catalog__stat">
            <dt className="model-catalog__stat-label">Providers</dt>
            <dd className="model-catalog__stat-value">{providers.length}</dd>
          </div>
          <div className="model-catalog__stat">
            <dt className="model-catalog__stat-label">Models</dt>
            <dd className="model-catalog__stat-value">{providers.reduce((sum, p) => sum + p.modelCount, 0)}</dd>
          </div>
          <div className="model-catalog__stat">
            <dt className="model-catalog__stat-label">Active providers</dt>
            <dd className="model-catalog__stat-value">{providers.filter((p) => p.isActive).length}</dd>
          </div>
        </dl>
      )}

      <div data-testid="model-catalog-list-grid">
        <CordioAbpPagedDataTable<LlmModelDto>
          className="model-catalog__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
        />
      </div>
    </div>
  );
};
